import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query"
import {
  CombinedWeatherData,
  NormalizedWeatherData,
  SummaryData,
} from "../types"
import { get } from "services/venue"
import { sendCaptureExceptionToSentry } from "utils/sentry"

export const fetchWeatherData = async <T>() => {
  try {
    const path = `/weather`
    const jsonRes = await get<T>(path)
    return jsonRes
  } catch (error) {
    sendCaptureExceptionToSentry({
      error,
      level: "error",
      transactionName: `Failed to fetch data from /weather endpoint.`,
    })

    throw error
  }
}

/**
 * React Query hook for fetching cached weather data from Cloudflare Worker (KV storage).
 *
 * The API aggregates OpenWeatherMap's current weather and air pollution data,
 * then returns a normalized object for UI display.
 *
 * @param options - Optional React Query options (e.g. refetchInterval, retry, onError, etc.)
 */
export const useCloudflareWeather = (
  options?: Partial<
    UseQueryOptions<CombinedWeatherData, Error, NormalizedWeatherData>
  >
): UseQueryResult<NormalizedWeatherData> => {
  return useQuery<CombinedWeatherData, Error, NormalizedWeatherData>({
    queryKey: ["weather"],
    queryFn: async (): Promise<CombinedWeatherData> => {
      const weatherData = await fetchWeatherData<CombinedWeatherData>()
      return weatherData
    },

    select: (json): NormalizedWeatherData => {
      const {
        icon_url: iconUrl,
        owm_aqi_level_concern: owmAqiLevelConcern,
        owm_aqi_ref: owmAqiRef,
        owm_aqi_value: owmAqiValue,
        us_aqi_value: usAqiValue,
        us_aqi_level_concern: usAqiLevelConcern,
        us_aqi_ref: usAqiRef,
        ...rest
      } = (json.summary || {}) as SummaryData

      return {
        ...rest,
        iconUrl,
        owmAqiLevelConcern,
        owmAqiRef,
        owmAqiValue,
        usAqiValue,
        usAqiLevelConcern,
        usAqiRef,
      }
    },
    staleTime: 60 * 60 * 1000, // default: 1 hour
    refetchOnWindowFocus: false,
    ...options,
  })
}
